'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, ReactNode, createContext, useContext } from 'react';
import { AppConfigContext } from '@/features/app-providers/app-config-provider';
import { resolveLegalModalContent } from '@/features/legal/lib/resolve-legal-modal-content';
import { Button } from '@/shared/ui/button';
import { ClientMDXRenderer } from '@/shared/ui/client-mdx-renderer';
import { Layout } from '@/shared/ui/layout';
import { Modal } from '@/shared/ui/modal';
import { ModalProps } from '@/shared/ui/modal/types';
import './terms-and-conditions-modal.scss';

type Props = ModalProps & {
    onAccept?: () => void;
    children?: ReactNode;
};

type TermsAndConditionsModalContextValue = {
    onAccept?: () => void;
    onClose: ModalProps['onClose'];
    content: string;
};

const TermsAndConditionsModalContext = createContext<TermsAndConditionsModalContextValue | null>(null);

const useTermsAndConditionsModalContext = () => {
    const context = useContext(TermsAndConditionsModalContext);
    if (!context) {
        throw new Error(
            'TermsAndConditionsModal components must be used within TermsAndConditionsModalWrapper.',
        );
    }
    return context;
};

const useTermsAndConditionsContent = () => {
    const t = useTranslations();
    const { termsAndConditions } = useContext(AppConfigContext);
    return resolveLegalModalContent(termsAndConditions, t('LEGAL_CONTENT.PREPARING'));
};

export const TermsAndConditionsModalWrapper = ({ onAccept, children, ...props }: Props) => {
    const content = useTermsAndConditionsContent();

    return (
        <TermsAndConditionsModalContext.Provider
            value={{
                onAccept,
                onClose: props.onClose,
                content,
            }}
        >
            <Modal
                {...props}
                wrapperProps={{
                    className: 'terms-and-conditions-modal',
                    containerProps: {
                        className: 'terms-and-conditions-modal__container',
                    },
                    'data-testid': 'terms-and-conditions-modal',
                }}
            >
                {children}
            </Modal>
        </TermsAndConditionsModalContext.Provider>
    );
};

/** Provider that only provides context and renders children (Modal prefab). */
export const TermsAndConditionsModalProvider = ({ onAccept, children, ...props }: Props) => {
    const content = useTermsAndConditionsContent();

    return (
        <TermsAndConditionsModalContext.Provider
            value={{
                onAccept,
                onClose: props.onClose,
                content,
            }}
        >
            {children}
        </TermsAndConditionsModalContext.Provider>
    );
};

/** Renders only the title text. Use inside prefab: "Modal.Title". */
const TermsAndConditionsModalTitle = () => {
    const t = useTranslations();
    return <>{t('TERMS_AND_CONDITIONS_MODAL.TITLE')}</>;
};

type ContentProps = ComponentProps<typeof Layout>;
const TermsAndConditionsModalContent = ({ media, ...props }: ContentProps) => {
    const { content } = useTermsAndConditionsModalContext();
    return (
        <Layout media={media} data-testid="terms-and-conditions-modal-content" {...props}>
            <ClientMDXRenderer content={content} />
        </Layout>
    );
};

type AcceptButtonProps = ComponentProps<typeof Button>;
const TermsAndConditionsModalAcceptButton = ({ onClick, ...props }: AcceptButtonProps) => {
    const t = useTranslations();
    const { onAccept, onClose } = useTermsAndConditionsModalContext();

    if (!onAccept) {
        return null;
    }

    return (
        <Layout
            element={Button}
            data-testid="terms-and-conditions-modal-accept-button"
            onClick={(event) => {
                onClick?.(event);
                onAccept?.();
                onClose();
            }}
            {...props}
        >
            {t('TERMS_AND_CONDITIONS_MODAL.ACCEPT_BUTTON')}
        </Layout>
    );
};

export { TermsAndConditionsModalWrapper as TermsAndConditionsModal };

export {
    TermsAndConditionsModalWrapper as Wrapper,
    TermsAndConditionsModalProvider as Provider,
    TermsAndConditionsModalTitle as Title,
    TermsAndConditionsModalContent as Content,
    TermsAndConditionsModalAcceptButton as AcceptButton,
};
